import { useCallback, useEffect, useRef, useState } from "react"

import { toast } from "@coval/ui"

import {
  buildCoviSummaryPreferencesRequest,
  EMPTY_SUMMARY_MEMORY,
  parseCoviSummaryMemoryResponse,
  readCoviSummaryMemory,
  subscribeCoviSummaryMemory,
  SUMMARY_MEMORY_API_PATH,
  writeCoviSummaryMemory,
  type CoviSummaryMemory,
} from "@/lib/covi/summaryMemory"
import { getOrganizationBearerHeaders } from "@/utils/auth/organization-token"
import { getRouteOrganizationSlug } from "@/utils/workspaceRoutes"

export const SUMMARY_MEMORY_MUTATION_TIMEOUT_MS = 15_000

type SummaryMemoryHeaders = Record<string, string>

/**
 * Bearer credentials for one summary-memory request, scoped to `orgSlug`.
 *
 * `/api/sofia/summary-preferences` resolves the caller's organization from the
 * request, and with no `Authorization` header that means the browser-global
 * `__session` cookie — one organization per browser profile, rewritten by whichever
 * tab last activated one. A read or a write issued from a background tab would land
 * on another organization's preferences. Clerk prefers a header token over the
 * cookie, so minting one pins the request to the organization this hook is editing.
 *
 * The panel callers take `orgSlug` from Clerk's *active* organization, which is
 * per-tab in-memory state but re-syncs from the session's server-side
 * last-active organization on token refresh — the same last-tab-wins signal as
 * the cookie. On a route that names an organization, that route is therefore
 * the only tab-local signal: a mismatch between it and `orgSlug` means this tab
 * has drifted onto another tab's organization, and the request must fail rather
 * than mint a valid token for it. Minting for the route's organization instead
 * is no better — the hook's optimistic state and cache are keyed on `orgSlug`,
 * so the client would record an edit the server applied elsewhere.
 *
 * Fails closed: when the route disagrees with `orgSlug`, or a session exists
 * but no token can be minted for `orgSlug`, the thrown error fails the load or
 * mutation rather than letting the request go out under another organization.
 */
async function getSummaryMemoryAuthorizationHeaders(
  orgSlug: string | null | undefined,
): Promise<SummaryMemoryHeaders> {
  if (!orgSlug) {
    return {}
  }

  if (typeof window !== "undefined") {
    const routeOrganizationSlug = getRouteOrganizationSlug(
      window.location.pathname,
    )
    if (routeOrganizationSlug !== null && routeOrganizationSlug !== orgSlug) {
      throw new Error(
        `This tab shows organization "${routeOrganizationSlug}", not "${orgSlug}".`,
      )
    }
  }

  return getOrganizationBearerHeaders(orgSlug)
}

// Naming the org keeps background tabs and cross-org visits scoped to the route.
function summaryMemoryUrl(organizationSlug: string): string {
  return `${SUMMARY_MEMORY_API_PATH}?organization=${encodeURIComponent(organizationSlug)}`
}

async function fetchSummaryMemory(
  headers: SummaryMemoryHeaders,
  organizationSlug: string,
): Promise<CoviSummaryMemory> {
  const response = await fetch(summaryMemoryUrl(organizationSlug), {
    method: "GET",
    headers,
  })
  if (!response.ok) {
    throw new Error(`Failed to load summary memory (${response.status})`)
  }
  return parseCoviSummaryMemoryResponse(await response.json())
}

async function saveSummaryMemory(
  memory: CoviSummaryMemory,
  headers: SummaryMemoryHeaders,
  signal: AbortSignal,
  organizationSlug: string,
): Promise<CoviSummaryMemory> {
  const response = await fetch(summaryMemoryUrl(organizationSlug), {
    method: "PUT",
    headers: { "Content-Type": "application/json", ...headers },
    body: JSON.stringify(buildCoviSummaryPreferencesRequest(memory)),
    signal,
  })
  if (!response.ok) {
    throw new Error(`Failed to save summary memory (${response.status})`)
  }
  return parseCoviSummaryMemoryResponse(await response.json())
}

async function deleteSummaryMemory(
  headers: SummaryMemoryHeaders,
  signal: AbortSignal,
  organizationSlug: string,
): Promise<CoviSummaryMemory> {
  const response = await fetch(summaryMemoryUrl(organizationSlug), {
    method: "DELETE",
    headers,
    signal,
  })
  if (!response.ok) {
    throw new Error(`Failed to clear summary memory (${response.status})`)
  }
  return parseCoviSummaryMemoryResponse(await response.json())
}

// Settles on timeout even when the underlying request ignores the abort signal,
// so a hung request cannot hold the head of the per-organization queue forever.
function requestWithTimeout(
  request: (signal: AbortSignal) => Promise<CoviSummaryMemory>,
): Promise<CoviSummaryMemory> {
  const controller = new AbortController()
  let timeoutId: ReturnType<typeof setTimeout> | undefined
  const timeout = new Promise<never>((_resolve, reject) => {
    timeoutId = setTimeout(() => {
      controller.abort()
      reject(new Error("Timed out waiting for the summary memory server"))
    }, SUMMARY_MEMORY_MUTATION_TIMEOUT_MS)
  })

  return Promise.race([request(controller.signal), timeout]).finally(() => {
    clearTimeout(timeoutId)
  })
}

interface SummaryMemoryMutation {
  errorTitle: string
  optimisticMemory: CoviSummaryMemory
  request: (signal: AbortSignal) => Promise<CoviSummaryMemory>
}

const optimisticSummaryMemoryWrites = new Set<string>()

export function useCoviSummaryMemory(orgSlug: string | null | undefined): {
  clearSummaryMemory: () => void
  canEditSummaryMemory: boolean
  setSummaryPreferencesText: (preferencesText: string) => void
  summaryMemory: CoviSummaryMemory
  summaryPreferencesText: string
} {
  const canEditSummaryMemory = Boolean(orgSlug)
  const orgSlugRef = useRef(orgSlug)
  const orgContextIdRef = useRef(0)
  const latestLoadIdRef = useRef(0)
  const nextMutationIdRef = useRef(0)
  const confirmedMemoryByOrgRef = useRef(new Map<string, CoviSummaryMemory>())
  const latestMutationIdByOrgRef = useRef(new Map<string, number>())
  // Survives unmount and organization switches so a late completion can still tell
  // whether a newer intent superseded it.
  const issuedMutationIdByOrgRef = useRef(new Map<string, number>())
  const mutationQueueByOrgRef = useRef(new Map<string, Promise<void>>())
  const [summaryMemory, setSummaryMemory] = useState<CoviSummaryMemory>(() =>
    readCoviSummaryMemory(orgSlug),
  )

  useEffect(() => {
    const confirmedMemoryByOrg = confirmedMemoryByOrgRef.current
    const latestMutationIdByOrg = latestMutationIdByOrgRef.current
    const mutationQueueByOrg = mutationQueueByOrgRef.current
    orgSlugRef.current = orgSlug
    orgContextIdRef.current += 1
    latestLoadIdRef.current += 1

    return () => {
      if (orgSlug && latestMutationIdByOrg.has(orgSlug)) {
        const confirmedMemory =
          confirmedMemoryByOrg.get(orgSlug) ?? readCoviSummaryMemory(orgSlug)
        const rolledBackMemory = writeCoviSummaryMemory(
          orgSlug,
          confirmedMemory,
        )
        confirmedMemoryByOrg.set(orgSlug, rolledBackMemory)
        latestMutationIdByOrg.delete(orgSlug)
        mutationQueueByOrg.delete(orgSlug)
      }
      if (orgSlugRef.current === orgSlug) {
        orgSlugRef.current = null
      }
      orgContextIdRef.current += 1
      latestLoadIdRef.current += 1
    }
  }, [orgSlug])

  const getConfirmedMemory = useCallback((targetOrgSlug: string) => {
    const confirmedMemory = confirmedMemoryByOrgRef.current.get(targetOrgSlug)
    if (confirmedMemory) {
      return confirmedMemory
    }

    const cachedMemory = readCoviSummaryMemory(targetOrgSlug)
    confirmedMemoryByOrgRef.current.set(targetOrgSlug, cachedMemory)
    return cachedMemory
  }, [])

  const persistConfirmedMemory = useCallback(
    (targetOrgSlug: string, memory: CoviSummaryMemory) => {
      const nextMemory = writeCoviSummaryMemory(targetOrgSlug, memory)
      confirmedMemoryByOrgRef.current.set(targetOrgSlug, nextMemory)
      return nextMemory
    },
    [],
  )

  const applyConfirmedMemory = useCallback(
    (targetOrgSlug: string, memory: CoviSummaryMemory) => {
      const nextMemory = persistConfirmedMemory(targetOrgSlug, memory)
      setSummaryMemory(nextMemory)
      return nextMemory
    },
    [persistConfirmedMemory],
  )

  const applyOptimisticMemory = useCallback(
    (targetOrgSlug: string, memory: CoviSummaryMemory) => {
      optimisticSummaryMemoryWrites.add(targetOrgSlug)
      let nextMemory: CoviSummaryMemory
      try {
        nextMemory = writeCoviSummaryMemory(targetOrgSlug, memory)
      } finally {
        optimisticSummaryMemoryWrites.delete(targetOrgSlug)
      }
      setSummaryMemory(nextMemory)
      return nextMemory
    },
    [],
  )

  const refreshSummaryMemory = useCallback(async () => {
    if (!orgSlug) {
      setSummaryMemory(EMPTY_SUMMARY_MEMORY)
      return
    }
    if (latestMutationIdByOrgRef.current.has(orgSlug)) {
      return
    }

    const orgContextId = orgContextIdRef.current
    const loadId = latestLoadIdRef.current + 1
    latestLoadIdRef.current = loadId
    try {
      const serverMemory = await fetchSummaryMemory(
        await getSummaryMemoryAuthorizationHeaders(orgSlug),
        orgSlug,
      )
      if (
        latestLoadIdRef.current !== loadId ||
        orgSlugRef.current !== orgSlug ||
        orgContextIdRef.current !== orgContextId
      ) {
        return
      }
      if (
        serverMemory.memoryEnabled === false &&
        !serverMemory.preferencesText
      ) {
        const cachedMemory = readCoviSummaryMemory(orgSlug)
        confirmedMemoryByOrgRef.current.set(orgSlug, cachedMemory)
        setSummaryMemory(cachedMemory)
        return
      }
      applyConfirmedMemory(orgSlug, serverMemory)
    } catch {
      if (
        latestLoadIdRef.current !== loadId ||
        orgSlugRef.current !== orgSlug ||
        orgContextIdRef.current !== orgContextId
      ) {
        return
      }
      const cachedMemory = readCoviSummaryMemory(orgSlug)
      confirmedMemoryByOrgRef.current.set(orgSlug, cachedMemory)
      setSummaryMemory(cachedMemory)
    }
  }, [applyConfirmedMemory, orgSlug])

  useEffect(() => {
    const cachedMemory = readCoviSummaryMemory(orgSlug)
    if (orgSlug) {
      confirmedMemoryByOrgRef.current.set(orgSlug, cachedMemory)
    }
    setSummaryMemory(cachedMemory)
    void refreshSummaryMemory()

    return subscribeCoviSummaryMemory((detail) => {
      if (detail?.orgSlug && detail.orgSlug !== orgSlug) {
        return
      }
      if (detail?.refresh) {
        void refreshSummaryMemory()
        return
      }
      const nextMemory = detail?.memory ?? readCoviSummaryMemory(orgSlug)
      if (orgSlug) {
        if (!optimisticSummaryMemoryWrites.has(orgSlug)) {
          confirmedMemoryByOrgRef.current.set(orgSlug, nextMemory)
        }
        if (latestMutationIdByOrgRef.current.has(orgSlug)) {
          return
        }
      }
      setSummaryMemory(nextMemory)
    })
  }, [orgSlug, refreshSummaryMemory])

  const enqueueMutation = useCallback(
    (targetOrgSlug: string, mutation: SummaryMemoryMutation) => {
      if (orgSlugRef.current !== targetOrgSlug) {
        return
      }

      const orgContextId = orgContextIdRef.current
      const mutationId = nextMutationIdRef.current + 1
      nextMutationIdRef.current = mutationId
      latestMutationIdByOrgRef.current.set(targetOrgSlug, mutationId)
      issuedMutationIdByOrgRef.current.set(targetOrgSlug, mutationId)
      latestLoadIdRef.current += 1
      applyOptimisticMemory(targetOrgSlug, mutation.optimisticMemory)

      const isCurrentContext = () =>
        orgSlugRef.current === targetOrgSlug &&
        orgContextIdRef.current === orgContextId
      const isLatestIntent = () =>
        issuedMutationIdByOrgRef.current.get(targetOrgSlug) === mutationId

      const runMutation = async () => {
        if (!isCurrentContext()) {
          if (
            latestMutationIdByOrgRef.current.get(targetOrgSlug) === mutationId
          ) {
            latestMutationIdByOrgRef.current.delete(targetOrgSlug)
          }
          return
        }

        try {
          const confirmedMemory = await requestWithTimeout(mutation.request)
          confirmedMemoryByOrgRef.current.set(targetOrgSlug, confirmedMemory)
          if (!isLatestIntent()) {
            return
          }

          // The server accepted this write, so the organization cache has to follow
          // even when the view unmounted or switched away mid-flight and rolled back.
          const nextMemory = persistConfirmedMemory(
            targetOrgSlug,
            confirmedMemory,
          )
          if (isCurrentContext()) {
            setSummaryMemory(nextMemory)
          }
        } catch (error) {
          if (!isLatestIntent()) {
            return
          }

          const restoredMemory = persistConfirmedMemory(
            targetOrgSlug,
            getConfirmedMemory(targetOrgSlug),
          )
          if (!isCurrentContext()) {
            return
          }

          setSummaryMemory(restoredMemory)
          toast.error({
            title: mutation.errorTitle,
            error,
            fallback: "Your previous summary memory was restored. Try again.",
          })
        } finally {
          if (
            latestMutationIdByOrgRef.current.get(targetOrgSlug) === mutationId
          ) {
            latestMutationIdByOrgRef.current.delete(targetOrgSlug)
          }
        }
      }

      const previousMutation = mutationQueueByOrgRef.current.get(targetOrgSlug)
      const queuedMutation = previousMutation
        ? previousMutation.then(runMutation, runMutation)
        : runMutation()
      mutationQueueByOrgRef.current.set(targetOrgSlug, queuedMutation)
      void queuedMutation.then(() => {
        if (
          mutationQueueByOrgRef.current.get(targetOrgSlug) === queuedMutation
        ) {
          mutationQueueByOrgRef.current.delete(targetOrgSlug)
        }
      })
    },
    [applyOptimisticMemory, getConfirmedMemory, persistConfirmedMemory],
  )

  const setSummaryPreferencesText = useCallback(
    (preferencesText: string) => {
      if (!orgSlug) {
        return
      }
      const optimisticMemory = parseCoviSummaryMemoryResponse({
        summary_preferences: { preferences_text: preferencesText },
      })
      enqueueMutation(orgSlug, {
        errorTitle: "Could not save summary memory",
        optimisticMemory,
        request: async (signal) =>
          saveSummaryMemory(
            optimisticMemory,
            await getSummaryMemoryAuthorizationHeaders(orgSlug),
            signal,
            orgSlug,
          ),
      })
    },
    [enqueueMutation, orgSlug],
  )

  const clearSummaryMemory = useCallback(() => {
    if (!orgSlug) {
      return
    }
    enqueueMutation(orgSlug, {
      errorTitle: "Could not clear summary memory",
      optimisticMemory: EMPTY_SUMMARY_MEMORY,
      request: async (signal) =>
        deleteSummaryMemory(
          await getSummaryMemoryAuthorizationHeaders(orgSlug),
          signal,
          orgSlug,
        ),
    })
  }, [enqueueMutation, orgSlug])

  return {
    clearSummaryMemory,
    canEditSummaryMemory,
    setSummaryPreferencesText,
    summaryMemory,
    summaryPreferencesText: summaryMemory.preferencesText,
  }
}
